import type { Option } from '@ksyos/design-system';
import { Alert, FormGroup, Select, TextInput } from '@ksyos/design-system';
import { useFormContext } from '@frontend/utils/hooks/useFormContext';
import type { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import {
    Policies,
    TfcV1DiagnosticReportBezoekAanSpecialist,
    urgenciesAccordingToGuideline,
    urgenciesForReferral,
} from '../../../../../../common/validation/customControls/ophthalmology';

export interface PolicyAndUrgencyCustomControlData {
    bezoek_aan_specialist_nu_nodig?: TfcV1DiagnosticReportBezoekAanSpecialist;
    conclusie_diagnose?: ConclusieDiagnoseOption;
    geadviseerd_beleid: string | null;
    geadviseerd_beleid_anders: string | null;
    urgentie: string | null;
}

interface IFundusscreeningPolicyAndUrgencyProps {
    advisedPolicies: Policies[];
    requiredPolicy?: boolean;
}

export function FundusscreeningPolicyAndUrgencyAddEditFields(props: Readonly<IFundusscreeningPolicyAndUrgencyProps>) {
    const form = useFormContext<PolicyAndUrgencyCustomControlData>();
    const isGeadviseerdBeleidRequired: boolean =
        props.requiredPolicy ||
        form.values.bezoek_aan_specialist_nu_nodig === TfcV1DiagnosticReportBezoekAanSpecialist.zieBeleid;
    const advisedPolicyOptions = getAdvisedPolicyOptions(props.advisedPolicies);

    return (
        <>
            <FormGroup
                isRequired={isGeadviseerdBeleidRequired}
                label="Geadviseerd beleid"
            >
                <Select
                    error={form.errors.getMessage('geadviseerd_beleid')}
                    name="geadviseerd_beleid"
                    onBlur={form.handleBlur}
                    onChange={(option) => {
                        form.handleChange([
                            {
                                name: 'geadviseerd_beleid',
                                value: option?.value ?? null,
                            },
                            {
                                name: 'geadviseerd_beleid_anders',
                                value: null,
                            },
                            { name: 'urgentie', value: null },
                        ]);
                    }}
                    options={advisedPolicyOptions}
                    placeholder="Kies uit de lijst"
                    value={
                        form.values.geadviseerd_beleid
                            ? advisedPolicyOptions.find((option) => option.value === form.values.geadviseerd_beleid)
                            : null
                    }
                />
            </FormGroup>
            {[
                Policies.controleVolgensRichtlijn,
                Policies.verwijzingOogartsDoorHuisarts,
                Policies.verwijzingOogartsDoorOptometrist,
            ].includes(form.values.geadviseerd_beleid as Policies) && (
                <FormGroup
                    isRequired={true}
                    label="Urgentie"
                >
                    <Select
                        error={form.errors.getMessage('urgentie')}
                        name="urgentie"
                        onBlur={form.handleBlur}
                        onChange={(option) => form.handleChange({ name: 'urgentie', value: option?.value ?? null })}
                        options={urgencyOptions(form.values)}
                        placeholder="Kies uit de lijst"
                        value={
                            form.values.urgentie &&
                            urgencyOptions(form.values).find((option) => option.value === form.values.urgentie)
                                ? { label: form.values.urgentie, value: form.values.urgentie }
                                : null
                        }
                    />
                </FormGroup>
            )}
            {form.values.geadviseerd_beleid === Policies.anders && (
                <FormGroup>
                    <TextInput
                        error={form.errors.getMessage('geadviseerd_beleid_anders')}
                        name="geadviseerd_beleid_anders"
                        onBlur={form.handleBlur}
                        onChange={form.handleChange}
                        placeholder="Beschrijf beleid"
                        value={form.values.geadviseerd_beleid_anders ?? ''}
                        variant="outline"
                    />
                </FormGroup>
            )}
            {(form.values.urgentie === 'Binnen 1 week bij oogarts' ||
                form.values.urgentie === 'Binnen 1 dag bij oogarts') && (
                <Alert appearance="info">
                    Let op: handel de verwijzing direct af. Verwijs de patiënt zelf naar de oogarts of bel de huisarts
                    voor een verwijzing. Indien dit niet mogelijk is, informeer Ksyos (020-6000060).
                </Alert>
            )}
        </>
    );
}

function getAdvisedPolicyOptions(advisedPolicies: Policies[]) {
    return advisedPolicies.map((policyValue) => {
        return { label: policyValue, value: policyValue } as Option;
    });
}

function urgencyOptions(customControlData: PolicyAndUrgencyCustomControlData) {
    if (customControlData.geadviseerd_beleid === Policies.controleVolgensRichtlijn) {
        return urgenciesAccordingToGuideline.map((policy) => {
            return { label: policy, value: policy };
        }) as Option[];
    } else if (
        [Policies.verwijzingOogartsDoorHuisarts, Policies.verwijzingOogartsDoorOptometrist].includes(
            customControlData.geadviseerd_beleid as Policies
        )
    ) {
        return urgenciesForReferral.map((policy) => {
            return { label: policy, value: policy };
        }) as Option[];
    } else {
        return [{ label: '', value: '' }] as Option[];
    }
}
